import { startOfDay } from 'date-fns';
import { z } from 'zod';
import type { PurchaseOrder } from '@/types/core/purchase-order/purchase-order';

const SENDING_DATE_FIELDS = {
  EOP: 'productionEndDate',
  ETD: 'expeditionDate',
  ETA: 'estimatedArrivalDate',
  LIS: 'deliveryDate',
} as const;

export const PURCHASE_ORDER_DATE_ORDER = ['eop', 'etd', 'eta', 'lis'] as const;
export type DateFieldKey = (typeof PURCHASE_ORDER_DATE_ORDER)[number];

function parseIsoDate(value: string | null | undefined): Date | undefined {
  if (!value) {
    return;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

export function getPurchaseOrderDates(
  purchaseOrder: Pick<
    PurchaseOrder,
    | 'productionEndDate'
    | 'expeditionDate'
    | 'estimatedArrivalDate'
    | 'deliveryDate'
  >,
) {
  return {
    eop: parseIsoDate(purchaseOrder.productionEndDate),
    etd: parseIsoDate(purchaseOrder.expeditionDate),
    eta: parseIsoDate(purchaseOrder.estimatedArrivalDate),
    lis: parseIsoDate(purchaseOrder.deliveryDate),
  };
}

export function getMissingPurchaseOrderDates(purchaseOrder: PurchaseOrder) {
  return Object.entries(SENDING_DATE_FIELDS)
    .filter(([, field]) => !purchaseOrder[field])
    .map(([label]) => label);
}

export const purchaseOrderDatesSchema = z
  .object({
    eop: z.date().optional(),
    etd: z.date().optional(),
    eta: z.date().optional(),
    lis: z.date().optional(),
  })
  .superRefine((data, context) => {
    let previous: { key: DateFieldKey; date: Date } | undefined;
    for (const key of PURCHASE_ORDER_DATE_ORDER) {
      const date = data[key];
      if (!date) {
        continue;
      }
      if (
        previous &&
        startOfDay(date).getTime() < startOfDay(previous.date).getTime()
      ) {
        context.addIssue({
          code: 'custom',
          path: [key],
          message: 'orderDateValidationChronology',
          params: {
            date: key.toUpperCase(),
            previousDate: previous.key.toUpperCase(),
          },
        });
      }
      previous = { key, date };
    }
  });

export type PurchaseOrderDates = z.infer<typeof purchaseOrderDatesSchema>;
